import { Avatar, Button, Card, Input, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { PropertyType, PtoV3ItemType, ResponsePagingType, SearchPTOParams } from 'Modules'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import LinkAddressCopy from 'component/link/link-address-copy'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { PTO_TYPE_OPTIONS } from 'constants/pto'
import { PTO_V3_SELLING_STATUS_CONFIG, PtoV3SellingStatusEnum } from 'constants/pto-v3'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { PAGES } from 'routes'
import { logError } from 'utils/logger'

import ButtonDeployContract from './button-deploy-contract'

import './style.scss'

const SORT_ORDER_MAP: Record<string, string> = {
  ascend: 'asc',
  descend: 'desc',
}

/**
 * PTO v3 list - ERC-3643 PTOs created through the PTOOrchestrator.
 * Shows the 3 addresses of a deployed PTO (token / ONCHAINID / compliance).
 */
const PTOV3 = () => {
  const [filteredData, setFilteredData] = useState<PtoV3ItemType[]>([])
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [total, setTotal] = useState(0)
  const [searchValue, setSearchValue] = useState('')

  const navigate = useNavigate()

  const additionalItems = [BREADCRUMBS.PTO_V3]

  const { canCRUD } = usePermission()

  const { searchParams, setSearchParams, initPagination, onChangePagination, onSearch } =
    useSearchParams<SearchPTOParams>()

  const disableButton = useMemo(() => !canCRUD(FEATURE.PTO), [canCRUD])

  const getData = useCallback(async (params?: SearchPTOParams) => {
    try {
      const res = await AppAdmin.getListPTOV3(params)

      if (res?.data.success) {
        const { results, totalResults } =
          res.data?.data || ({} as ResponsePagingType<PtoV3ItemType>)
        setTotal(totalResults || 0)
        setFilteredData(results || [])
      }
    } catch (error) {
      setTotal(0)
      setFilteredData([])
      logError('Error: ', error)
    }
  }, [])

  const getListPropertyTypes = async () => {
    try {
      const res = await AppAdmin.getListProperty()
      if (res?.data.success) {
        setPropertyTypes(
          res?.data.data.map((item: PropertyType) => ({
            ...item,
            id: Number(item.id),
          }))
        )
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: SorterResult<PtoV3ItemType> | SorterResult<PtoV3ItemType>[]
  ) => {
    onChangePagination(pagination)

    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sort =
      singleSorter.field && singleSorter.order
        ? `${singleSorter.field}:${SORT_ORDER_MAP[singleSorter.order]}`
        : undefined

    setSearchParams((prev) => ({ ...prev, sort }))
  }

  useEffect(() => {
    getData(searchParams)
  }, [searchParams, getData])

  useEffect(() => {
    getListPropertyTypes()
  }, [])

  const renderAddress = (address?: string) => <LinkAddressCopy address={address} max={6} />

  const columns: ColumnsType<PtoV3ItemType> = [
    {
      title: 'Logo',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: (logo) => <Avatar src={logo} size={50} />,
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      render: (text) => {
        const typeObj = propertyTypes.find((type) => type.id === text)
        return typeObj ? typeObj.name : text
      },
    },
    {
      title: 'Token Name',
      dataIndex: 'contractName',
      key: 'contractName',
      render: (text) => text || '-',
    },
    {
      title: 'Token Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (text) => (text ? <Tag>{text}</Tag> : '-'),
    },
    {
      title: 'PTO Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: renderAddress,
    },
    {
      title: 'OnchainID',
      dataIndex: 'onchainId',
      key: 'onchainId',
      render: renderAddress,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: true,
    },
    {
      title: 'Contract',
      key: 'contract',
      render: (_, record) =>
        record.contractAddress ? (
          <Tag color="success">Deployed</Tag>
        ) : (
          <Tag color="default">Not Deployed</Tag>
        ),
    },
    {
      title: 'Selling Status',
      dataIndex: 'sellingStatus',
      key: 'sellingStatus',
      render: (sellingStatus?: PtoV3SellingStatusEnum) => {
        const config =
          PTO_V3_SELLING_STATUS_CONFIG[sellingStatus ?? PtoV3SellingStatusEnum.OFF_SALE] ||
          PTO_V3_SELLING_STATUS_CONFIG[PtoV3SellingStatusEnum.OFF_SALE]
        return <Tag color={config.color}>{config.label}</Tag>
      },
    },
    {
      title: 'Total Sold',
      dataIndex: 'mint',
      key: 'mint',
      render: (_, record) => `${record.mint ?? 0} / ${record.total ?? 0}`,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          {!record.contractAddress && !disableButton ? (
            <ButtonDeployContract id={record._id} onSuccess={() => getData(searchParams)} />
          ) : null}
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`${PAGES.PTO_V3}/${record._id}`)
            }}
          >
            View
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="main-section">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title">PTO ERC-3643 Admin</div>
      <div className="table-row">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="pto-v3-search">
            <Input
              allowClear
              placeholder="Search by name or contract address..."
              value={searchValue}
              style={{ width: 260 }}
              onChange={(e) => {
                setSearchValue(e.target.value)
                // clicking the clear "x" resets the applied keyword immediately
                if (!e.target.value) {
                  onSearch({ keyword: '' } as SearchPTOParams)
                }
              }}
              onPressEnter={() => onSearch({ keyword: searchValue } as SearchPTOParams)}
            />
            <Select
              allowClear
              placeholder="All Types"
              value={searchParams.ptoType}
              onChange={(value) => onSearch({ ptoType: value } as SearchPTOParams)}
              options={PTO_TYPE_OPTIONS}
              style={{ width: 120 }}
            />
            <Button
              type="primary"
              onClick={() => onSearch({ keyword: searchValue } as SearchPTOParams)}
            >
              Search
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button
              disabled={disableButton}
              type="primary"
              onClick={() => {
                navigate(PAGES.PTO_V3_CREATE)
              }}
            >
              Create
            </Button>
          </div>
        </div>
        <Card>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record._id}
            pagination={total <= DEFAULT_PAGE_SIZE ? false : initPagination(total)}
            onChange={handleTableChange}
            scroll={{ x: 1600 }}
          />
        </Card>
      </div>
    </div>
  )
}

export default PTOV3
